/**
 * The row buffer, wired to a scroll container and a server.
 *
 * Everything the buffer *is* lives in `rowWindow.ts` and `rowRange.ts`, which are
 * pure. This is what makes it move: it holds one window, turns scroll positions
 * into requests, drops answers to questions nobody is asking any more, and hands
 * the pure functions the results.
 *
 * A **factory, not a singleton**, from the first line. A board is N windows, one
 * per stack, and every view on screen wants its own; retrofitting that onto a
 * module-level store is the expensive version of this decision. Each store is
 * created for one bucket and destroyed with it.
 *
 * The fetcher is a parameter rather than an import, so the tests below this drive
 * a real store with a function that resolves whatever they like -- there is no
 * network in any of them, and no mock of `fetch` either.
 */
import { createStore, type StoreApi } from 'zustand/vanilla'

import type { HeldRow } from './rowHold'

import type { RowData } from '@/api/types'
import {
  DEFAULT_REQUEST_SIZE,
  MAX_WINDOW_BLOCKS,
  planFetch,
  planWindow,
  rangeLength,
  rangesEqual,
  type RowRange,
} from './rowRange'
import {
  applyCount,
  applyFetchedRows,
  deleteRow,
  emptyWindow,
  insertRow,
  moveRow,
  updateRow,
  type RowWindow,
} from './rowWindow'
import { mergeGroupByMetadata, type GroupByMetadata } from '../grid/grouping'
import type { WindowOperation } from './rowEvents'
import type { RowFetcher } from './rowFetcher'
import type { RowQuery } from './rowQuery'
import {
  initialScrollSchedule,
  planScrollFire,
  type ScrollScheduleState,
} from './scrollSchedule'

/** How tall the scroll container is, and how tall one row is inside it. */
export interface RowViewport {
  height: number
  rowHeight: number
}

/** The default until a container has measured itself. */
export const DEFAULT_VIEWPORT: RowViewport = { height: 0, rowHeight: 33 }

export interface RowWindowState {
  window: RowWindow
  query: RowQuery | null
  viewport: RowViewport
  scrollTop: number
  /** The rows on screen: what a virtualizer renders, and nothing more. */
  visible: RowRange
  /**
   * How many rows each group holds, accumulated across the pages fetched.
   *
   * Separate from the window rather than part of it, because it is not a fact
   * about the rows held: a page counts the *whole* of every group its rows fall
   * into, and those counts stay true for rows the window has since trimmed.
   * Cleared when the query changes, since a different question has different
   * groups.
   */
  groupByMetadata: GroupByMetadata
  /**
   * Rows that would have left the view but are being held in place.
   *
   * Keyed by row id. A row is here because an edit made in this tab stopped it
   * matching the filters, or moved where it belongs, while the selection was
   * still on it -- see `rowHold`. The grid badges these; nothing else reads
   * them.
   */
  held: ReadonlyMap<number, HeldRow>
  /**
   * Rows whose create or update has not come back yet.
   *
   * A set rather than a flag on the row, because a row is a server payload and
   * this is a fact about a request. The grid shows it so that a row which
   * appeared instantly is distinguishable from one that is actually saved.
   */
  saving: ReadonlySet<number>
  /** Whether a request for more rows is outstanding. */
  loading: boolean
  /**
   * Whether the server has answered this query at all yet.
   *
   * Not the same as `count === 0`, and the difference is visible: a view with
   * no rows and a view whose first page has not landed are the same window, and
   * a surface that reads the count alone says "no rows" to somebody who is
   * still waiting for theirs. The grid gets away without it because an empty
   * grid still draws its header; a gallery of no cards is a blank rectangle.
   *
   * False again whenever the query changes, because the new question has not
   * been answered either.
   */
  answered: boolean
  /** The last failure, or null. Cleared by anything that succeeds. */
  error: unknown
}

export interface RowWindowActions {
  /**
   * Points the window at a query.
   *
   * A no-op when the signature is unchanged, which is the common case rather
   * than the rare one: the view is a TanStack Query cache entry, so every
   * refetch hands over a new object describing the same question. Comparing the
   * signature rather than the reference is what stops a background refetch from
   * throwing the buffer away and scrolling the reader back to the top.
   */
  setQuery: (query: RowQuery) => void
  setViewport: (viewport: RowViewport) => void
  scrolled: (scrollTop: number) => void
  /** Re-reads the window from the server, keeping the reader where they are. */
  refresh: () => void

  insertRowAt: (row: RowData, index: number) => void
  updateRowInPlace: (row: RowData) => void
  /**
   * Swaps a row for one with a different id, in place.
   *
   * For a row created here: it goes in with a temporary id so it can be seen
   * and typed into immediately, and the server's answer replaces it. A remove
   * and an insert would do the same thing to the array and the wrong thing to
   * the count, which has already been raised.
   */
  replaceRowId: (rowId: number, row: RowData) => void
  removeRow: (rowId: number, index?: number) => void
  moveRowTo: (rowId: number, toIndex: number) => void
  setCount: (count: number) => void

  /** Holds a row in place, with what to do to it once it is let go. */
  holdRow: (rowId: number, held: HeldRow) => void
  /**
   * Lets held rows go, applying whatever was deferred for each.
   *
   * Takes a list rather than one id because the selection moving can release
   * several at once -- a pasted block is one edit over many rows -- and one
   * `set` for the lot means the grid re-renders once.
   */
  releaseRows: (rowIds: readonly number[]) => void
  /**
   * Forgets holds without acting on them.
   *
   * For an edit the server refused: the values have been put back, so the row
   * never stopped matching and never moved, and the departure that was waiting
   * describes a state that no longer exists. Releasing it would apply it.
   */
  discardHolds: (rowIds: readonly number[]) => void

  /** Marks a row as having a write outstanding, or no longer having one. */
  setSaving: (rowId: number, saving: boolean) => void

  /**
   * Aborts anything outstanding and cancels the pending scroll decision.
   *
   * Not final. React 19's StrictMode mounts a component, tears it down and
   * mounts it again, so a store torn down in an effect's cleanup is one a live
   * component is about to keep using. Pointing it at a query again revives it.
   */
  destroy: () => void
}

export type RowWindowStore = StoreApi<RowWindowState & RowWindowActions>

/**
 * How many rows to ask for at a time, and how many to keep either side.
 *
 * `rowPadding` is half a viewport, so the visible range is the viewport exactly
 * -- upstream computes the same thing as `ceil(height / rowHeight / 2)`.
 *
 * `requestSize` is normally the flat default, but it is raised to cover the
 * visible range on a very tall container. The window may span
 * `MAX_WINDOW_BLOCKS` request blocks and is trimmed to that, so a viewport
 * taller than one block would otherwise have rows trimmed out from under the
 * reader the moment they arrived. Tying the two together makes the invariant
 * hold by construction instead of by hoping nobody opens a tall window.
 */
export function geometryFor(viewport: RowViewport): {
  rowPadding: number
  requestSize: number
} {
  if (viewport.rowHeight <= 0 || viewport.height <= 0) {
    return { rowPadding: 0, requestSize: DEFAULT_REQUEST_SIZE }
  }

  const rowPadding = Math.ceil(viewport.height / viewport.rowHeight / 2)
  return {
    rowPadding,
    requestSize: Math.max(DEFAULT_REQUEST_SIZE, rowPadding * 2 + 1),
  }
}

/** One request, remembered so an identical one is not issued twice. */
interface Outstanding {
  signature: string
  range: RowRange
  controller: AbortController
}

export interface RowWindowStoreOptions {
  fetchRows: RowFetcher
  /** Injected so the velocity policy can be driven by a test without timers. */
  now?: () => number
}

export function createRowWindowStore({
  fetchRows,
  now = () => Date.now(),
}: RowWindowStoreOptions): RowWindowStore {
  // Deliberately outside the store's state: none of it is rendered, and putting
  // a timeout id or an AbortController in a subscribable store means every
  // component re-renders when a request starts.
  let outstanding: Outstanding | null = null
  let schedule: ScrollScheduleState = initialScrollSchedule(now())
  let deferred: ReturnType<typeof setTimeout> | null = null
  let destroyed = false

  const store = createStore<RowWindowState & RowWindowActions>((set, get) => {
    function cancelDeferred(): void {
      if (deferred !== null) {
        clearTimeout(deferred)
        deferred = null
      }
    }

    function abortOutstanding(): void {
      if (outstanding !== null) {
        outstanding.controller.abort()
        outstanding = null
      }
      schedule = { ...schedule, fetching: false }
    }

    function request(query: RowQuery, range: RowRange): void {
      // The query is passed in rather than read: every caller has already
      // established there is one, and reading it back would mean a guard here
      // that nothing can reach. The range is never empty either -- `planFetch`
      // reports "already held" as no request at all.

      // The same question, already asked. Upstream compares the offset and limit
      // of the last request for the same reason: a scroll that settles back
      // where it was must not re-issue the request that is already in flight.
      if (
        outstanding !== null &&
        outstanding.signature === query.signature &&
        rangesEqual(outstanding.range, range)
      ) {
        return
      }

      abortOutstanding()

      const controller = new AbortController()
      const attempt: Outstanding = { signature: query.signature, range, controller }
      outstanding = attempt
      schedule = { ...schedule, fetching: true }
      set({ loading: true })

      void fetchRows(query, range, controller.signal)
        .then((page) => {
          // One question, asked by identity: is this still *the* request? It
          // covers both ways an answer goes stale -- the query changed, or a
          // later scroll superseded this range -- because every path that does
          // either aborts what is outstanding and replaces it first. Comparing
          // the window's signature as well would be a second guard for the same
          // cases, which is a guard no test can distinguish.
          if (destroyed || outstanding !== attempt) return
          outstanding = null
          schedule = { ...schedule, fetching: false }

          set((state) => ({
            window: applyFetchedRows(
              state.window,
              page.rows,
              attempt.range.start,
              // An answer that did not carry a count is not an answer that the
              // view is empty; keep what we had.
              page.count ?? state.window.count,
              { requestSize: geometryFor(state.viewport).requestSize },
            ),
            groupByMetadata:
              page.groupByMetadata === undefined
                ? state.groupByMetadata
                : mergeGroupByMetadata(state.groupByMetadata, page.groupByMetadata),
            loading: false,
            answered: true,
            error: null,
          }))
          // The count that just arrived can move the window, so the plan is
          // re-run rather than assumed to be unchanged.
          plan()
        })
        .catch((error: unknown) => {
          // An abort rejects here too, and needs no separate branch: aborting is
          // only ever done by whatever superseded this request, so the identity
          // check has already sent it away.
          if (destroyed || outstanding !== attempt) return
          outstanding = null
          schedule = { ...schedule, fetching: false }
          // Answered, in the sense that matters here: the wait is over, and
          // what is on screen is now this window's final say until something
          // asks again.
          set({ loading: false, answered: true, error })
        })
    }

    /**
     * Recomputes which rows are on screen, and returns what should be held.
     *
     * Cheap and synchronous -- index arithmetic over numbers already in hand --
     * so it runs on every scroll event rather than on the throttled schedule.
     * Which rows are *rendered* has to follow the scroll immediately or the
     * container shows the rows for where the reader was a tenth of a second
     * ago; which rows are *fetched* is the expensive question, and that is the
     * one the velocity policy defers.
     *
     * This is also why there is no second virtualizer over the rows. The visible
     * range and the buffer are answers to the same arithmetic, and computing
     * them in two places is how a grid ends up rendering rows it never asked
     * for.
     */
    function replan(): { buffer: RowRange } | null {
      const { window, query, viewport, scrollTop } = get()
      if (query === null) return null

      const { rowPadding, requestSize } = geometryFor(viewport)
      const planned = planWindow({
        scrollTop,
        viewportHeight: viewport.height,
        rowHeight: viewport.rowHeight,
        count: window.count,
        rowPadding,
        requestSize,
      })

      set({ visible: planned.visible })
      return planned
    }

    /** Asks for whatever the plan wants and the window does not hold. */
    function fetchMissing(plan: { buffer: RowRange } | null): void {
      const { window, query } = get()
      if (query === null || plan === null) return

      const fetch = planFetch(window.range, plan.buffer)
      // When nothing needs fetching the window already covers what is wanted.
      // It is not trimmed back: the rows are held, they are bounded by
      // `trimToLimit` on the way in, and dropping them would only mean fetching
      // them again on the way back up.
      if (fetch.request !== null) request(query, fetch.request)
    }

    /** Both halves, for everything that is not a scroll event. */
    function plan(): void {
      fetchMissing(replan())
    }

    /**
     * The first read of a query, before anything is known about the table.
     *
     * It cannot come from the plan: the window's count is zero until a response
     * says otherwise, so `planWindow` would return an empty range and nothing
     * would ever be fetched. Two blocks is what the plan asks for at the top of
     * a table once the count is known, so asking for it up front means the
     * common case takes exactly one request.
     */
    function load(query: RowQuery): void {
      const { requestSize } = geometryFor(get().viewport)
      request(query, { start: 0, end: requestSize * Math.min(2, MAX_WINDOW_BLOCKS) })
    }

    return {
      window: emptyWindow(),
      query: null,
      groupByMetadata: {},
      viewport: DEFAULT_VIEWPORT,
      scrollTop: 0,
      visible: { start: 0, end: 0 },
      held: new Map<number, HeldRow>(),
      saving: new Set<number>(),
      loading: false,
      answered: false,
      error: null,

      setQuery: (query) => {
        // Revives a store torn down by a StrictMode cleanup. `destroy` clears
        // the query as well as aborting, so this is never the early return
        // below: a store that was destroyed always reloads.
        destroyed = false

        if (get().query?.signature === query.signature) {
          // Same question, new object. Keep the rows and the scroll position;
          // only the identity the store holds is refreshed, so a later
          // comparison is against the current object.
          set({ query })
          return
        }

        cancelDeferred()
        abortOutstanding()
        set({
          query,
          window: emptyWindow(query.signature),
          // Not kept across a change of question. The counts belong to the
          // groups of the view as it was asked about, and a filter that removes
          // half the rows changes every one of them.
          groupByMetadata: {},
          visible: { start: 0, end: 0 },
          // A different question has different answers about which rows belong,
          // so nothing held against the old one still means anything -- and the
          // rows they were about are gone from the window with it.
          held: new Map<number, HeldRow>(),
          saving: new Set<number>(),
          // The new question has not been answered either.
          answered: false,
          error: null,
        })
        load(query)
      },

      setViewport: (viewport) => {
        const current = get().viewport
        if (
          current.height === viewport.height &&
          current.rowHeight === viewport.rowHeight
        ) {
          return
        }
        set({ viewport })
        // Straight to the plan rather than through the scheduler: a container
        // being measured is not the reader scrolling, and throttling it would
        // leave the first paint a beat behind the layout that caused it. Safe
        // before anything has loaded, because a plan over an empty view is a
        // window of no rows and a request for none.
        plan()
      },

      scrolled: (scrollTop) => {
        set({ scrollTop })
        // Always, before the scheduler is consulted: the rows on screen follow
        // the scrollbar, and only the request for more of them is throttled.
        const planned = replan()

        const { decision, state } = planScrollFire(schedule, {
          scrollTop,
          now: now(),
        })
        schedule = state

        cancelDeferred()
        if (decision.fire) {
          fetchMissing(planned)
          return
        }

        // The trailing edge. A reader dragging the scrollbar produces one fetch
        // -- for wherever they let go -- rather than one per block passed.
        deferred = setTimeout(() => {
          deferred = null
          schedule = { ...schedule, scrollTop: get().scrollTop, at: now() }
          plan()
        }, decision.deferMs)
      },

      refresh: () => {
        const { window, query } = get()
        if (query === null) return
        // Whatever is held, read again. Used when something changed that the
        // client cannot reproduce -- a formula recalculated, rows reordered by
        // somebody else -- so the range is kept and only the contents renewed.
        abortOutstanding()
        if (rangeLength(window.range) === 0) load(query)
        else request(query, window.range)
      },

      insertRowAt: (row, index) => {
        set((state) => ({
          window: insertRow(state.window, row, index, {
            requestSize: geometryFor(state.viewport).requestSize,
          }),
        }))
        plan()
      },

      updateRowInPlace: (row) => {
        set((state) => ({ window: updateRow(state.window, row) }))
      },

      replaceRowId: (rowId, row) => {
        set((state) => {
          const offset = state.window.rows.findIndex((held) => held.id === rowId)
          if (offset === -1) return state

          const rows = [...state.window.rows]
          rows[offset] = row
          return { window: { ...state.window, rows } }
        })
      },

      removeRow: (rowId, index) => {
        set((state) => ({ window: deleteRow(state.window, rowId, index) }))
        plan()
      },

      moveRowTo: (rowId, toIndex) => {
        set((state) => ({ window: moveRow(state.window, rowId, toIndex) }))
      },

      setCount: (count) => {
        set((state) => ({ window: applyCount(state.window, count) }))
        plan()
      },

      holdRow: (rowId, held) => {
        set((state) => {
          const next = new Map(state.held)
          next.set(rowId, held)
          return { held: next }
        })
      },

      releaseRows: (rowIds) => {
        const { held } = get()
        const releasing = rowIds.filter((rowId) => held.has(rowId))
        if (releasing.length === 0) return

        const deferred = releasing.flatMap((rowId) => held.get(rowId)?.deferred ?? [])

        // Dropped from `held` first, so a row cannot be both badged and gone:
        // the operations below remove or move it, and a badge that outlived its
        // row would be drawn against whatever slid into that index.
        set((state) => {
          const next = new Map(state.held)
          for (const rowId of releasing) next.delete(rowId)
          return { held: next }
        })

        // Through the store's own actions, like every other caller, so the
        // count and the range are maintained the one way.
        applyRowOperations(store, deferred)
      },

      discardHolds: (rowIds) => {
        set((state) => {
          if (!rowIds.some((rowId) => state.held.has(rowId))) return {}
          const next = new Map(state.held)
          for (const rowId of rowIds) next.delete(rowId)
          return { held: next }
        })
      },

      setSaving: (rowId, saving) => {
        set((state) => {
          if (state.saving.has(rowId) === saving) return {}
          const next = new Set(state.saving)
          if (saving) next.add(rowId)
          else next.delete(rowId)
          return { saving: next }
        })
      },

      destroy: () => {
        destroyed = true
        cancelDeferred()
        abortOutstanding()
        // The query goes with it. Without that, a StrictMode remount calls
        // `setQuery` with the signature the store already has, takes the "same
        // question" path, and never asks for the rows it just abandoned -- a
        // grid that is permanently empty in development and fine in production.
        set({ query: null })
      },
    }
  })

  return store
}

/**
 * Drives a window from the decisions `rowEvents` made about it.
 *
 * The split is deliberate: deciding what should happen to a row is pure and
 * testable against plain arrays, and this is the three lines that do it.
 */
export function applyRowOperations(
  store: RowWindowStore,
  operations: readonly WindowOperation[],
): void {
  const actions = store.getState()
  for (const operation of operations) {
    switch (operation.type) {
      case 'insert':
        actions.insertRowAt(operation.row, operation.index)
        break
      case 'update':
        actions.updateRowInPlace(operation.row)
        break
      case 'remove':
        actions.removeRow(operation.rowId)
        break
      case 'move':
        actions.moveRowTo(operation.rowId, operation.toIndex)
        break
    }
  }
}
